import ChildlessPlaceholder from 'client/components/core/ChildlessPlaceholder'
import DropBoxUi from 'client/components/core/ChildrenEntities/components/drop-box-ui'
import DropBoxWithTextUi from 'client/components/core/ChildrenEntities/components/drop-box-with-text-ui'
import { EmptyWebsiteBody } from 'client/components/core/ChildrenEntities/components/empty-website-body'
import LibraryElementEnum from 'client/enums/LibraryElementEnum'
import useManagementHook from 'client/hooks/useManagement'
import { createPage } from 'client/pages'
import { isCreateBlockDialogActive } from 'client/reducers/managementReducer'
import { pageSelectors, typedPageSelectors, usePage } from 'client/store/index'
import { addStructure } from 'client/store/page/pageActions'
import structureTypes from 'common/constants/structureTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import React from 'react'
import { useDrop } from 'react-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

export interface DropBoxEmptyProps {
  type?: EntityTypeEnum
  placeholderText: string | null
  parentId: string
  allowedTypes: string[]
  createEntity: (
    type: unknown,
    parent: string,
    index: number,
  ) => React.ReactNode
  createBlock: (value: string) => React.ReactNode
  moveEntity: (value: unknown) => React.ReactNode
}

const DropBoxEmpty = ({
  type,
  placeholderText = null,
  parentId,
  allowedTypes = [],
  createEntity,
  createBlock,
  moveEntity,
}: DropBoxEmptyProps) => {
  const { t } = useTranslation()
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const dispatch = useDispatch()
  const parent = usePage(page => pageSelectors.getEntityById(page, parentId))
  const pageType = usePage(typedPageSelectors.getPageType)
  const isCreateBlockActive = useManagementHook(isCreateBlockDialogActive)
  const isWebsiteRoot = type === EntityTypeEnum.WebsitePageBody
  const [{ canDrop }, connectDrop] = useDrop({
    accept: allowedTypes,
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return
      }

      //@TODO: casting should be remove when correct typings are introduced
      const draggedItemType = monitor.getItemType() as LibraryElementEnum

      if (draggedItemType.startsWith('TOOLBOX_')) {
        createEntity(draggedItemType, parentId, 0)
      } else if (draggedItemType in LibraryElementEnum) {
        const { createLibraryElementStructure } = createPage(pageType)
        const structure = createLibraryElementStructure({
          libraryElementType: draggedItemType,
          container: parent,
          locale: pageLocale,
          position: 0,
        })
        //@ts-ignore @TODO types for react-dnd
        dispatch(addStructure(structure))
      } else if (item.type === 'BLOCK') {
        //@ts-ignore @TODO types for react-dnd
        createBlock(item.entities, parentId, 0, item.isMaster)
      } else {
        //@ts-ignore @TODO types for react-dnd
        moveEntity(item.entity, parentId, 0)
      }
    },
    collect: monitor => ({
      canDrop: monitor.isOver() && monitor.canDrop(),
    }),
    canDrop: (draggable, monitor) => {
      if (draggable.type === 'BLOCK') {
        if (
          monitor.isOver({ shallow: true }) &&
          //@ts-ignore @TODO types for react-dnd
          draggable.entities.find(
            //@ts-ignore @TODO types for react-dnd
            ent =>
              ent.type === structureTypes.SECTION ||
              ent.type === EntityTypeEnum.Section,
          ) &&
          !allowedTypes.includes(structureTypes.SECTION) &&
          !allowedTypes.includes(EntityTypeEnum.Section)
        ) {
          return false
        }

        return true
      }
      // checking for library elements
      if (
        //@ts-ignore @TODO types for react-dnd
        draggable.type.startsWith('TOOLBOX_') ||
        //@ts-ignore @TODO types for react-dnd
        draggable.type.startsWith('Library')
      ) {
        return true
      }

      // check if draggable item under the child dropBox
      //@ts-ignore @TODO types for react-dnd
      if (draggable.entity.id === parentId) {
        return false
      }
      // checking descendant dropBoxes
      //@ts-ignore @TODO types for react-dnd
      if (draggable?.descendantIds.includes(parentId)) {
        return false
      }

      if (
        //@ts-ignore @TODO types for react-dnd
        draggable.allowedEntityIds &&
        //@ts-ignore @TODO types for react-dnd
        !draggable.allowedEntityIds.includes(parentId)
      ) {
        return false
      }

      return true
    },
  })

  return (
    <DropBoxUi
      ref={connectDrop}
      //@ts-ignore @TODO types for react-dnd
      canDrop={canDrop}
      dropCenter={!placeholderText}
      styles={{ position: 'relative', zIndex: 1 }} /* overlap bodyUI */
    >
      {isWebsiteRoot && <EmptyWebsiteBody />}
      {!isWebsiteRoot &&
        (placeholderText ? (
          <DropBoxWithTextUi canDrop={canDrop}>
            {t(placeholderText)}
          </DropBoxWithTextUi>
        ) : (
          //@ts-ignore @TODO types for react-dnd
          <ChildlessPlaceholder isCreateBlockActive={isCreateBlockActive} />
        ))}
    </DropBoxUi>
  )
}

export default DropBoxEmpty
